import { useEffect, useRef } from "react";
import { AlertTriangle, CopyX, Loader2, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AlertDialog, AlertDialogContent, AlertDialogDescription, AlertDialogFooter,
  AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { useDedupeTemplates } from "./useDedupeTemplates";

export const DedupeTemplatesDialog = () => {
  const { open, changeOpen, pin, setPin, review, preview, error, busy, verify, confirm } = useDedupeTemplates();
  const cancelRef = useRef(null);
  useEffect(() => {
    if (preview) cancelRef.current?.focus();
  }, [preview]);

  const removableTotal = review?.removable_total || 0;

  return (
    <AlertDialog open={open} onOpenChange={changeOpen}>
      <AlertDialogTrigger asChild>
        <Button data-testid="admin-dedupe-button" variant="outline" className="border-amber-500/40 text-amber-300 hover:bg-amber-500/10">
          <CopyX className="h-4 w-4 mr-1" /> Find Duplicates
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent data-testid="dedupe-dialog" className="w-[calc(100%-2rem)] max-w-2xl max-h-[90dvh] overflow-y-auto rounded-lg bg-[hsl(222_22%_8%)] border-white/10 text-white">
        <AlertDialogHeader className="text-left">
          <div data-testid="dedupe-step-label" className="flex items-center gap-2 text-xs font-mono text-amber-300">
            {preview ? <AlertTriangle className="h-4 w-4" /> : <ShieldCheck className="h-4 w-4" />}
            {preview ? "STEP 2 OF 2 · FINAL CONFIRMATION" : "STEP 1 OF 2 · REVIEW & PASSCODE"}
          </div>
          <AlertDialogTitle data-testid="dedupe-dialog-title" className="font-display text-lg">
            {preview ? "Confirm duplicate removal" : "Duplicate templates (same artwork)"}
          </AlertDialogTitle>
          <AlertDialogDescription data-testid="dedupe-dialog-description" className="text-white/65">
            {preview
              ? "This removes the extra copies across the entire catalogue. One copy of each image is always kept, and nothing with minted cards or reserved print-edition codes is ever removed. This cannot be undone."
              : "A duplicate is a template whose artwork exactly matches another. The oldest copy (or a copy that already has minted/printed cards) is kept; only empty extras are eligible for removal."}
          </AlertDialogDescription>
        </AlertDialogHeader>

        {error && <p role="alert" data-testid="dedupe-error" className="text-sm text-red-300 break-words">{error}</p>}

        {preview ? (
          <>
            <div className="border-y border-white/10 py-4 space-y-2" data-testid="dedupe-review-confirm">
              <p data-testid="dedupe-removable-count" className="text-lg font-semibold">{preview.removable_count.toLocaleString()} duplicate template{preview.removable_count === 1 ? "" : "s"}</p>
              <p className="text-sm text-white/65">Across {preview.group_count.toLocaleString()} artwork group{preview.group_count === 1 ? "" : "s"}.</p>
              <p data-testid="dedupe-protected-notice" className="text-sm text-emerald-300">One copy per image is kept. Minted cards and reserved print-edition codes are never removed.</p>
              {preview.removable_count === 0 && <p className="text-sm text-white/65">No removable duplicates.</p>}
            </div>
            <AlertDialogFooter className="gap-2 sm:space-x-0">
              <Button ref={cancelRef} type="button" data-testid="dedupe-final-cancel-button" variant="outline" disabled={busy} onClick={() => changeOpen(false)} className="border-white/15 bg-transparent text-white hover:bg-white/5">Cancel</Button>
              <Button type="button" data-testid="dedupe-confirm-button" disabled={busy || !preview.removable_count || !preview.confirmation_token} onClick={confirm} className="bg-amber-500 text-black hover:bg-amber-400 whitespace-normal h-auto min-h-10">
                {busy ? <Loader2 className="h-4 w-4 mr-2 shrink-0 animate-spin" /> : <CopyX className="h-4 w-4 mr-2 shrink-0" />}
                {busy ? "Removing…" : `Yes, remove ${preview.removable_count.toLocaleString()}`}
              </Button>
            </AlertDialogFooter>
          </>
        ) : (
          <>
            <div className="border-y border-white/10 py-4 space-y-3" data-testid="dedupe-review">
              {busy && !review && (
                <div className="flex items-center gap-2 text-white/60 text-sm" data-testid="dedupe-scanning"><Loader2 className="h-4 w-4 animate-spin" /> Scanning the catalogue…</div>
              )}
              {review && (
                <>
                  <p data-testid="dedupe-summary" className="text-lg font-semibold">
                    {removableTotal.toLocaleString()} removable duplicate{removableTotal === 1 ? "" : "s"}
                    <span className="text-white/50 text-sm font-normal"> · {review.group_count.toLocaleString()} artwork group{review.group_count === 1 ? "" : "s"}</span>
                  </p>
                  {removableTotal === 0 ? (
                    <p data-testid="dedupe-empty" className="text-sm text-white/65">No exact-duplicate templates found. Your catalogue is clean.</p>
                  ) : (
                    <div data-testid="dedupe-group-grid" className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2 max-h-72 overflow-y-auto pr-1">
                      {review.groups.map((g) => (
                        <div key={g.front_image} data-testid="dedupe-group" className="relative rounded-md overflow-hidden border border-white/10 bg-[hsl(220_18%_8%)]">
                          <div className="aspect-[2.5/3.5] overflow-hidden">
                            {g.front_image && <img src={g.front_image} alt="" loading="lazy" className="w-full h-full object-cover" />}
                          </div>
                          <div className="absolute top-1 right-1 rounded-full bg-amber-500 text-black text-[10px] font-bold px-1.5 py-0.5" title={`${g.total} copies total`}>×{g.total}</div>
                          <div className="px-1 py-1">
                            <div className="text-[9px] text-white/70 truncate" title={g.title}>{g.title}</div>
                            <div className="text-[9px] text-amber-300 font-mono">−{g.remove_count}{g.has_protected ? " · kept: minted" : ""}</div>
                          </div>
                        </div>
                      ))}
                      {review.shown < review.group_count && (
                        <div className="col-span-full text-[11px] text-white/40 text-center pt-1">Showing {review.shown} of {review.group_count} groups — all will be de-duplicated.</div>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
            <form onSubmit={verify} className="space-y-4" data-testid="dedupe-passcode-form">
              <div className="space-y-2">
                <Label htmlFor="dedupe-admin-passcode" data-testid="dedupe-passcode-label">Admin passcode</Label>
                <Input id="dedupe-admin-passcode" data-testid="dedupe-passcode-input" type="password" inputMode="numeric" autoComplete="off" maxLength={10} value={pin} disabled={busy || removableTotal === 0} onChange={(event) => setPin(event.target.value.replace(/[^0-9]/g, ""))} aria-invalid={Boolean(error)} className="bg-[hsl(220_18%_12%)] border-white/15" />
              </div>
              <AlertDialogFooter className="gap-2 sm:space-x-0">
                <Button type="button" data-testid="dedupe-passcode-cancel-button" variant="outline" disabled={busy} onClick={() => changeOpen(false)} className="border-white/15 bg-transparent text-white hover:bg-white/5">Cancel</Button>
                <Button type="submit" data-testid="dedupe-verify-passcode-button" disabled={busy || removableTotal === 0 || !/^[0-9]{4,10}$/.test(pin)} className="bg-cyan-400 text-black hover:bg-cyan-300">
                  {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}{busy ? "Verifying…" : "Verify & review"}
                </Button>
              </AlertDialogFooter>
            </form>
          </>
        )}
      </AlertDialogContent>
    </AlertDialog>
  );
};
